/**
 * Utility functions for handling user avatars
 */

/**
 * Converts a Google profile image URL to a more reliable format
 * that's less likely to be blocked by CORS or rate limiting
 */
export const getOptimizedAvatarUrl = (avatarUrl: string, size: number = 96): string => {
  if (!avatarUrl) return '';
  
  // Handle Google profile images
  if (avatarUrl.includes('googleusercontent.com')) {
    // Remove any existing size parameters and query strings
    let baseUrl = avatarUrl.split('=')[0].split('?')[0];
    
    // Ensure we're using the lh3 subdomain for better reliability
    if (!baseUrl.includes('lh3.googleusercontent.com')) {
      baseUrl = baseUrl.replace(/lh\d+\.googleusercontent\.com/, 'lh3.googleusercontent.com');
    }
    
    // Add size parameter - use different formats based on URL structure
    if (baseUrl.includes('/a/')) {
      // For URLs with /a/ structure, use =s{size}-c format
      return `${baseUrl}=s${size}-c`;
    } else {
      // For direct image URLs, try =s{size} format first
      return `${baseUrl}=s${size}`;
    }
  }
  
  // For non-Google URLs, return as-is
  return avatarUrl;
};

/**
 * Gets the appropriate avatar URL for display, with fallback handling
 */
export const getDisplayAvatarUrl = (
  user: { id: string; avatar?: string }, 
  size: number = 96,
  useProxy: boolean = false
): string => {
  if (!user.avatar) return '';
  
  // If using proxy for Google images
  if (useProxy && user.avatar.includes('googleusercontent.com')) {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    return `${apiUrl}/auth/avatar/${user.id}`;
  }
  
  // Use optimized URL
  return getOptimizedAvatarUrl(user.avatar, size);
};

/**
 * Gets a fallback proxy URL for when the optimized URL fails
 */
export const getProxyAvatarUrl = (user: { id: string; avatar?: string }): string => {
  if (!user.avatar || !user.avatar.includes('googleusercontent.com')) return '';
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  return `${apiUrl}/auth/avatar/${user.id}`;
};

/**
 * Checks if an avatar URL is from Google
 */
export const isGoogleAvatar = (avatarUrl?: string): boolean => {
  return Boolean(avatarUrl?.includes('googleusercontent.com'));
}; 